import { IEntryInfo } from '../_Core/CoreTypes';
import {
  AwardRank,
  IAwardInfo,
  ICustomSlide,
  INamedPerson,
  ISlideInfo,
  ISlideShowItem,
  ISlideShowResponse,
  ISponsorInfo,
  IWinnerInfo,
} from './SlideShowTypes';

const THANKS_PER_SLIDE = 18;
const HONOR_PER_SLIDE = 4;
const SPONSORS_PER_SLIDE = 6;
const SPECIAL_PER_SLIDE = 3;

export function awardRank(name?: string): AwardRank {
  const n = (name || '').toLowerCase();
  if (/\b(1st|first|gold|grand)\b/.test(n)) return 'first';
  if (/\b(2nd|second|silver)\b/.test(n)) return 'second';
  if (/\b(3rd|third|bronze)\b/.test(n)) return 'third';
  return 'honor';
}

function sponsorLogo(sponsors: ISponsorInfo[] | undefined, name?: string): string | undefined {
  if (!name || !sponsors) return undefined;
  const needle = name.trim().toLowerCase();
  return sponsors.find((s) => (s.name || '').trim().toLowerCase() === needle)?.logoAddress;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function sortWinners(winners: IWinnerInfo[]): IWinnerInfo[] {
  return [...winners].sort((a, b) => (a.Points ?? 0) - (b.Points ?? 0));
}

function theme(slideData?: ISlideShowItem, extras?: { logoUrl?: string; fairName?: string }) {
  return {
    BackgroundColor: slideData?.PrimaryColor || '#101318',
    TextColor: slideData?.TextColor || '#f4f1ea',
    PrimaryColor: slideData?.PrimaryColor || '#101318',
    AccentColor: slideData?.AccentColor || '',
    BackgroundImageUrl: slideData?.BackgroundImageUrl,
    LogoUrl: extras?.logoUrl,
    FairName: extras?.fairName,
  };
}

function namesOf(people?: INamedPerson[]): string[] {
  return (people || []).map((p) => p.name).filter((n) => !!n);
}

function insertCustom(slides: ISlideInfo[], customs: ICustomSlide[] | undefined, after: string, look: ReturnType<typeof theme>) {
  (customs || [])
    .filter((c) => (c.InsertAfter || 'end').toLowerCase() === after.toLowerCase())
    .forEach((c, i) => {
      slides.push({
        ...look,
        Key: `custom:${c.Id || after}:${i}`,
        Kind: 'custom',
        Title: c.Title,
        SubTitle: c.SubTitle,
        Description: c.Body,
        ImageUrl: c.ImageUrl,
      });
    });
}

function pushPagedWinners(
  slides: ISlideInfo[],
  look: ReturnType<typeof theme>,
  keyPrefix: string,
  title: string,
  subTitle: string | undefined,
  description: string | undefined,
  winners: IWinnerInfo[],
  special: boolean
) {
  const ranked = sortWinners(winners).map((w) => ({ ...w, Rank: w.Rank || awardRank(w.AwardSubType) }));
  const featured = ranked.filter((w) => w.Rank !== 'honor');
  const honor = ranked.filter((w) => w.Rank === 'honor');

  featured.forEach((w, i) => {
    slides.push({
      ...look,
      Key: `${keyPrefix}:feat:${w.AwardId}:${w.ProjectId}:${i}`,
      Kind: 'winner',
      Title: title,
      SubTitle: subTitle,
      Description: description,
      Winners: [w],
      NeedsReveal: true,
    });
  });

  const per = special ? SPECIAL_PER_SLIDE : HONOR_PER_SLIDE;
  chunk(honor.length ? honor : featured.length ? [] : ranked, per).forEach((group, i) => {
    if (!group.length) return;
    const one = group.length === 1;
    slides.push({
      ...look,
      Key: `${keyPrefix}:grp:${i}`,
      Kind: one ? 'winner' : 'winners',
      Title: title,
      SubTitle: subTitle,
      Description: description,
      Winners: group,
      NeedsReveal: true,
    });
  });
}

export function buildSlides(
  slideData: ISlideShowItem | undefined,
  data: ISlideShowResponse | undefined,
  extras: { logoUrl?: string; fairName?: string; recapUrl?: string }
): ISlideInfo[] {
  if (!slideData || !data) return [];

  const look = theme(slideData, extras);
  const slides: ISlideInfo[] = [];
  const projectDic: Record<number, IEntryInfo> = {};
  data.projects?.forEach((p) => {
    projectDic[p.id] = p;
  });
  const awardsDic: Record<number, IAwardInfo> = {};
  data.categoryAwards?.forEach((a) => {
    awardsDic[a.id] = a;
  });

  const assignedDic: Record<string, IWinnerInfo[]> = {};
  data.assignedCategoryAwards?.forEach((a) => {
    const p = projectDic[a.projectId];
    const award = awardsDic[a.awardId];
    if (!p || !award) return;
    const winner: IWinnerInfo = {
      Title: p.title,
      ProjectId: p.project_id,
      AwardSubType: award.name,
      AwardId: award.id,
      Points: award.points,
      Owners: p.ownerString,
      School: p.school,
      Category: p.categoryName,
      Division: p.DivisionName,
      PresentedBy: award.presentedBy,
      Sponsor: award.sponsor,
      SponsorLogo: sponsorLogo(data.sponsors, award.sponsor),
      Rank: awardRank(award.name),
    };
    const cateKey = `${p.category_id}`;
    const divKey = `${p.category_id}:${p.DivisionId}`;
    assignedDic[cateKey] = [...(assignedDic[cateKey] || []), winner];
    assignedDic[divKey] = [...(assignedDic[divKey] || []), winner];
  });

  slides.push({
    ...look,
    Key: 'title',
    Kind: 'title',
    Title: slideData.Title || extras.fairName,
    SubTitle: slideData.Description,
  });
  insertCustom(slides, slideData.CustomSlides, 'title', look);

  const pushSection = (key: string, title: string, subTitle: string | undefined, body: () => void, keepEmpty?: boolean) => {
    const start = slides.length;
    slides.push({
      ...look,
      Key: `section:${key}`,
      Kind: 'section',
      Title: title,
      SubTitle: subTitle,
    });
    body();
    if (!keepEmpty && slides.length === start + 1) slides.pop();
    insertCustom(slides, slideData.CustomSlides, key, look);
  };

  slideData.AutoPages?.forEach((page) => {
    switch (page.toUpperCase()) {
      case 'CATEGORYAWARDSBYDIVISION':
        pushSection('CategoryAwardsByDivision', 'Category Awards', 'By Division', () => {
          data.categories?.forEach((cat) => {
            data.divisions?.forEach((d) => {
              const assigned = assignedDic[`${cat.id}:${d.id}`];
              if (!assigned?.length) return;
              pushPagedWinners(slides, look, `catdiv:${cat.id}:${d.id}`, cat.name, d.name, undefined, assigned, false);
            });
          });
        });
        break;
      case 'CATEGORYAWARDS':
        pushSection('CategoryAwards', 'Category Awards', undefined, () => {
          data.categories?.forEach((cat) => {
            const assigned = assignedDic[`${cat.id}`];
            if (!assigned?.length) return;
            pushPagedWinners(slides, look, `cat:${cat.id}`, cat.name, undefined, undefined, assigned, false);
          });
        });
        break;
      case 'SPECIALAWARDS':
        pushSection('SpecialAwards', 'Special Awards', undefined, () => {
          data.specialAwards?.forEach((award) => {
            const assigned = data.assignedSpecialAwards?.filter((y) => y.awardId === award.id) ?? [];
            if (!assigned.length) return;
            const winners: IWinnerInfo[] = [];
            assigned.forEach((w) => {
              const project = projectDic[w.projectId];
              if (!project) return;
              winners.push({
                Title: project.title,
                ProjectId: project.project_id,
                Owners: project.ownerString,
                School: project.school,
                Category: project.categoryName,
                Division: project.DivisionName,
                AwardSubType: w.subType || award.name,
                AwardId: award.id,
                Points: award.points,
                PresentedBy: award.presentedBy,
                Sponsor: award.sponsor,
                SponsorLogo: sponsorLogo(data.sponsors, award.sponsor),
                Rank: awardRank(w.subType || award.name),
              });
            });
            pushPagedWinners(slides, look, `sp:${award.id}`, award.name, award.description, undefined, winners, true);
          });
        });
        break;
      case 'SPONSORS':
        pushSection('Sponsors', 'Thanks to our Sponsors', "We couldn't do it without their generous support.", () => {
          const sponsors = [...(data.sponsors || [])];
          chunk(sponsors, SPONSORS_PER_SLIDE).forEach((group, i) => {
            slides.push({
              ...look,
              Key: `sponsors:${i}`,
              Kind: 'sponsors',
              Title: 'Thank You',
              Sponsors: group,
            });
          });
        }, true);
        break;
      case 'THANKYOUJUDGES': {
        const names = namesOf(data.judges);
        pushSection('ThankYouJudges', 'Thank You Judges', names.length ? `${names.length} judges made this possible.` : 'Thank you for your time and effort.', () => {
          chunk(names, THANKS_PER_SLIDE).forEach((group, i) => {
            slides.push({
              ...look,
              Key: `judges:${i}`,
              Kind: 'thanks',
              Title: 'Thank You Judges',
              Names: group,
            });
          });
        }, true);
        break;
      }
      case 'THANKYOUVOLUNTEER': {
        const names = namesOf(data.volunteers);
        pushSection(
          'ThankYouVolunteer',
          'Thank You Volunteers',
          names.length ? `${names.length} volunteers made this possible.` : 'Thank you for your time and effort, you make all of this possible.',
          () => {
            chunk(names, THANKS_PER_SLIDE).forEach((group, i) => {
              slides.push({
                ...look,
                Key: `volunteers:${i}`,
                Kind: 'thanks',
                Title: 'Thank You Volunteers',
                Names: group,
              });
            });
          },
          true
        );
        break;
      }
    }
  });

  insertCustom(slides, slideData.CustomSlides, 'end', look);
  const knownAfter = new Set(['title', 'end', ...(slideData.AutoPages || [])].map((x) => x.toLowerCase()));
  (slideData.CustomSlides || [])
    .filter((c) => !knownAfter.has((c.InsertAfter || 'end').toLowerCase()))
    .forEach((c, i) => {
      slides.push({
        ...look,
        Key: `custom:orphan:${c.Id || i}`,
        Kind: 'custom',
        Title: c.Title,
        SubTitle: c.SubTitle,
        Description: c.Body,
        ImageUrl: c.ImageUrl,
      });
    });

  if (extras.recapUrl) {
    slides.push({
      ...look,
      Key: 'recap',
      Kind: 'recap',
      Title: 'Take the results with you',
      SubTitle: 'Scan for the full recap of tonight\'s winners.',
      RecapUrl: extras.recapUrl,
    });
  }

  return slides;
}

export function slideDurationSeconds(slide: ISlideInfo | undefined, configured?: number, kiosk?: boolean): number {
  const base = configured && configured > 0 ? configured : kiosk ? 7 : 0;
  if (!base || !slide) return base;
  if (slide.Kind === 'winner' || slide.Kind === 'winners') return Math.round(base * 1.4);
  if (slide.Kind === 'sponsors' || slide.Kind === 'thanks') return Math.round(base * 1.2);
  if (slide.Kind === 'recap') return Math.round(base * 1.6);
  return base;
}
